// Layer 1 (Data Schema) — typed facts for the maintenance event log (a20-maintenance S1b).
//
// The route reads a year-to-date stoppage history, not just the trailing 30-day
// basis. The rows inside the basis are NOT typed here: they are generated from
// `DOWNTIME_STATES`, so the log and the Downtime timeline can never disagree.
// What this file owns is the typed history *before* the basis (HISTORY_EVENTS),
// the per-item physical buffer each event is absorbed by (ITEM_BUFFER_HOURS),
// the equipment classes the reliability read groups its items into
// (EQUIPMENT_CLASSES), and the two named targets that read draws against.
//
// Facts only — no derivation. `hours` is never typed on an event; the event-log
// projection computes it from the two timestamps. Nothing here imports a
// transform, and `downtime.seed.ts` imports nothing from here: the dependency
// runs one way.
//
// mock-only: seeded YTD downtime history; a POC reads this from the CMMS /
// historian feed rather than from a seeded array (ADR-0012). The site
// configuration below (buffers, class targets) is durable — entered once.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { CauseCategoryId, DowntimeItemId } from './downtime.seed';

export { CAUSE_CATEGORIES } from './downtime.seed';
export type { CauseCategoryId };

/** The log opens on 01 Jan 2026 — the first day of the seeded operating year. */
export const YTD_START: SeriesTime = '2026-01-01T00:00';

export type DowntimeEventKind = 'planned' | 'unplanned';

/** One typed stoppage before the 30-day basis. `hours` is never typed here —
 *  the transform computes it from the two timestamps. */
export type HistoryEvent = {
  itemId: DowntimeItemId;
  kind: DowntimeEventKind;
  start: SeriesTime;
  end: SeriesTime;
  causeCategory: CauseCategoryId;
  subCause: string;
  note?: string;
  workOrderId?: string;
};

// ---------------------------------------------------------------------------
// HISTORY_EVENTS — hand-authored stoppages from YTD_START up to BASIS_START
// (2026-05-09T00:00). Authored item-major, each item's rows ascending by start,
// one row per item per calendar day so no two rows of one item can overlap.
//
// 12 planned rows: four shutdown days (21 Jan, 18 Feb, 18 Mar, 15 Apr), each
// carrying three different items, each row 06:00 → 06:00 + 4…12 h. No item
// carries an unplanned row on its own shutdown day.
//
// 62 unplanned rows: CR01 8, AF01 7, BM01 10, GP01 8, PB-RGH 7, ZN-RGH 7,
// TK03 7, GP40 8; every item has at least one row in each of January, February,
// March and April, and all eight items carry a row in the 01–08 May window.
// Each item's unplanned start hours alternate day-shift and night-shift hours,
// so all four crews appear. Durations 1–14 h, most under 6 h.
//
// Every start and end minute is :00 or :30, so every duration is a half-hour
// multiple and the totals close exactly.
// ---------------------------------------------------------------------------

export const HISTORY_EVENTS: readonly HistoryEvent[] = [
  // CR01
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-01-06T08:00',
    end: '2026-01-06T15:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Crusher liner wear',
    note: 'Mantle liner wear found on inspection — chamber opened and liner rotated',
    workOrderId: 'WO-1042',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-01-14T22:30',
    end: '2026-01-15T03:30',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feed blockage',
    note: 'Oversize rock jam at the crusher throat cleared by hand',
    workOrderId: 'WO-1055',
  },
  {
    itemId: 'CR01',
    kind: 'planned',
    start: '2026-01-21T06:00',
    end: '2026-01-21T12:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Liner inspection',
    note: 'Shutdown day — crusher chamber opened for a liner inspection',
    workOrderId: 'WO-850',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-02-04T11:00',
    end: '2026-02-04T13:30',
    causeCategory: 'external-supply',
    subCause: 'Supply undervoltage',
    note: 'Site supply dip tripped the crusher drive — drive restarted once the supply held steady',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-02-24T19:00',
    end: '2026-02-25T04:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Crusher liner wear',
    note: 'Concave wear measured out of tolerance — segment replaced',
    workOrderId: 'WO-1091',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-03-05T14:30',
    end: '2026-03-05T16:30',
    causeCategory: 'electrical-trips',
    subCause: 'Motor protection trip',
    note: 'Crusher motor protection relay tripped on overcurrent — reset after winding inspection',
    workOrderId: 'WO-1120',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-03-26T02:00',
    end: '2026-03-26T13:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Crusher liner wear',
    note: 'Mantle liner wear — chamber opened and liner rotated',
    workOrderId: 'WO-1146',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-04-08T09:30',
    end: '2026-04-08T12:30',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feed blockage',
    note: 'Rock jam at the crusher throat cleared by hand',
  },
  {
    itemId: 'CR01',
    kind: 'planned',
    start: '2026-04-15T06:00',
    end: '2026-04-15T14:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Liner inspection',
    note: 'Shutdown day — mantle rotated and chamber gap re-set',
    workOrderId: 'WO-904',
  },
  {
    itemId: 'CR01',
    kind: 'unplanned',
    start: '2026-05-04T21:00',
    end: '2026-05-05T00:30',
    causeCategory: 'external-supply',
    subCause: 'Grid frequency event',
    note: 'Grid frequency excursion tripped the crusher drive — drive restarted once the supply stabilised',
    workOrderId: 'WO-1218',
  },

  // AF01
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-01-08T07:00',
    end: '2026-01-08T12:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feeder drive & belt wear',
    note: 'Apron feeder chain tension out of spec — chain and pans adjusted',
    workOrderId: 'WO-1044',
  },
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-01-23T20:00',
    end: '2026-01-23T21:00',
    causeCategory: 'external-supply',
    subCause: 'Supply undervoltage',
    note: 'Supply dip tripped the feeder drive — drive restarted once the supply held steady',
  },
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-02-09T13:00',
    end: '2026-02-09T21:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feeder drive & belt wear',
    note: 'Feeder drive coupling insert replaced after visible wear',
    workOrderId: 'WO-1078',
  },
  {
    itemId: 'AF01',
    kind: 'planned',
    start: '2026-02-18T06:00',
    end: '2026-02-18T10:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Belt inspection',
    note: 'Shutdown day — feeder belt splices inspected and re-tensioned',
    workOrderId: 'WO-868',
  },
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-03-11T23:00',
    end: '2026-03-12T10:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feeder drive & belt wear',
    note: 'Feeder belt splice opened up — belt re-spliced and re-tensioned',
    workOrderId: 'WO-1126',
  },
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-03-30T10:00',
    end: '2026-03-30T11:00',
    causeCategory: 'instrument-failures',
    subCause: 'Transmitter fault',
    note: 'Stockpile level transmitter drifted — recalibrated against the belt scale',
  },
  {
    itemId: 'AF01',
    kind: 'planned',
    start: '2026-04-15T06:00',
    end: '2026-04-15T12:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Drive chain service',
    note: 'Shutdown day — feeder drive chain and sprockets serviced',
    workOrderId: 'WO-906',
  },
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-04-20T18:30',
    end: '2026-04-21T00:00',
    causeCategory: 'electrical-trips',
    subCause: 'Cable insulation failure',
    note: 'Feeder drive cable insulation failed at the gland — cable section renewed',
    workOrderId: 'WO-1187',
  },
  {
    itemId: 'AF01',
    kind: 'unplanned',
    start: '2026-05-06T06:30',
    end: '2026-05-06T13:30',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feeder drive & belt wear',
    note: 'Head pulley bearing running hot — bearing replaced during the stoppage',
    workOrderId: 'WO-1221',
  },

  // BM01
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-01-05T09:00',
    end: '2026-01-05T13:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Feed blockage',
    note: 'Mill feed chute blocked on a tramp metal plate — chute cleared',
    workOrderId: 'WO-1041',
  },
  {
    itemId: 'BM01',
    kind: 'planned',
    start: '2026-01-21T06:00',
    end: '2026-01-21T18:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Planned reline',
    note: 'Planned mill reline — shell liner rotation over the shutdown day',
    workOrderId: 'WO-852',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-01-28T21:30',
    end: '2026-01-29T01:30',
    causeCategory: 'electrical-trips',
    subCause: 'Motor protection trip',
    note: 'Mill motor protection relay tripped — stator winding insulation tested before restart',
    workOrderId: 'WO-1061',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-02-06T12:00',
    end: '2026-02-06T13:30',
    causeCategory: 'instrument-failures',
    subCause: 'Transmitter fault',
    note: 'Trunnion bearing temperature probe failed open — probe replaced',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-02-26T19:30',
    end: '2026-02-27T06:30',
    causeCategory: 'pump-failures',
    subCause: 'Bearing failure',
    note: 'Lube oil pump bearing failed — pump cartridge replaced',
    workOrderId: 'WO-1096',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-03-03T16:00',
    end: '2026-03-03T19:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Mill mechanical trip',
    note: 'Discharge grate plugged with grinding media — grate cleared',
  },
  // [syn] March mill-stop duration reconciles M1 availability to Planning's 91.0%.
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-03-14T00:00',
    end: '2026-03-15T13:30',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Mill drive bearing inspection',
    note: '[syn] Extended bearing inspection during the March ramp profile.',
    workOrderId: 'WO-1301',
  },
  {
    itemId: 'BM01',
    kind: 'planned',
    start: '2026-03-18T06:00',
    end: '2026-03-18T18:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Planned reline',
    note: 'Planned mill reline — shell liner rotation over the shutdown day',
    workOrderId: 'WO-890',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-03-24T01:00',
    end: '2026-03-24T05:30',
    causeCategory: 'electrical-trips',
    subCause: 'VSD fault trip',
    note: 'Mill drive VSD faulted on a gate turn-off — drive module replaced',
    workOrderId: 'WO-1143',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-04-07T08:30',
    end: '2026-04-07T10:00',
    causeCategory: 'instrument-failures',
    subCause: 'Flow meter drift',
    note: 'Mill feed water flow meter drifted — meter recalibrated',
  },
  // [syn] April mill-stop duration reconciles M2 availability to Planning's 92.4%.
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-04-25T00:00',
    end: '2026-04-26T21:30',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Mill shell liner inspection',
    note: '[syn] Extended liner inspection during the April ramp profile.',
    workOrderId: 'WO-1302',
  },
  {
    itemId: 'BM01',
    kind: 'unplanned',
    start: '2026-05-02T22:00',
    // [syn] Duration recalibrated against the M3 Planning availability of 93.7%.
    end: '2026-05-03T14:00',
    causeCategory: 'mill-crusher-mechanical',
    subCause: 'Mill mechanical trip',
    note: '[syn] Trunnion bearing temperature trip; duration recalibrated to the M3 Planning availability, then bearing inspected and oil gallery flushed.',
    workOrderId: 'WO-1213',
  },

  // GP01
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-01-09T10:00',
    end: '2026-01-09T16:00',
    causeCategory: 'pump-failures',
    subCause: 'Gland seal failure',
    note: 'Cyclone feed pump mechanical seal failed — seal cartridge replaced',
    workOrderId: 'WO-1045',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-01-19T19:00',
    end: '2026-01-19T20:30',
    causeCategory: 'cyclone-wear',
    subCause: 'Cyclone spigot wear',
    note: 'Spigot wear on the two worst cyclones — spigots replaced',
    workOrderId: 'WO-1057',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-02-03T15:30',
    end: '2026-02-04T00:30',
    causeCategory: 'pump-failures',
    subCause: 'Impeller erosion',
    note: 'Impeller erosion reduced pump head — impeller replaced',
    workOrderId: 'WO-1072',
  },
  {
    itemId: 'GP01',
    kind: 'planned',
    start: '2026-02-18T06:00',
    end: '2026-02-18T16:00',
    causeCategory: 'pump-failures',
    subCause: 'Impeller wear check',
    note: 'Shutdown day — cyclone feed pump pulled down for an impeller measurement',
    workOrderId: 'WO-870',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-02-23T20:00',
    end: '2026-02-23T22:00',
    causeCategory: 'cyclone-wear',
    subCause: 'Cyclone spigot wear',
    note: 'Cyclone apex worn through on the primary cluster — apex renewed',
    workOrderId: 'WO-1090',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-03-06T08:00',
    end: '2026-03-06T10:30',
    causeCategory: 'pump-failures',
    subCause: 'Suction strainer blockage',
    note: 'Pump suction strainer blocked with debris — strainer cleaned',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-03-20T18:30',
    end: '2026-03-20T21:30',
    causeCategory: 'cyclone-wear',
    subCause: 'Feed distributor wear',
    note: 'Cyclone feed distributor worn — distributor liner replaced',
    workOrderId: 'WO-1138',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-04-13T11:30',
    end: '2026-04-13T15:30',
    causeCategory: 'pump-failures',
    subCause: 'Discharge valve leak',
    note: 'Pump discharge isolation valve leaking past the seat — valve replaced',
  },
  {
    itemId: 'GP01',
    kind: 'unplanned',
    start: '2026-05-07T23:00',
    end: '2026-05-08T03:00',
    causeCategory: 'cyclone-wear',
    subCause: 'Cyclone liner wear',
    note: 'Cyclone body liner worn through on the rougher cluster — liner replaced',
    workOrderId: 'WO-1223',
  },

  // PB-RGH
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-01-12T06:00',
    end: '2026-01-12T08:00',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'Lime dosing blockage',
    note: 'Lime dosing line blocked at the injection point — line flushed',
    workOrderId: 'WO-1048',
  },
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-01-26T23:30',
    end: '2026-01-27T01:00',
    causeCategory: 'instrument-failures',
    subCause: 'pH probe fault',
    note: 'Rougher pH probe fouled — probe cleaned and recalibrated',
  },
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-02-11T14:00',
    end: '2026-02-11T15:00',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'Froth pH excursion',
    note: 'Rougher pH ran above the control band — lime dosing trimmed back',
  },
  {
    itemId: 'PB-RGH',
    kind: 'planned',
    start: '2026-02-18T06:00',
    end: '2026-02-18T14:00',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'Reagent dosing calibration',
    note: 'Shutdown day — reagent dosing loops calibrated against the assay',
    workOrderId: 'WO-872',
  },
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-03-10T20:30',
    end: '2026-03-10T23:30',
    causeCategory: 'instrument-failures',
    subCause: 'Transmitter fault',
    note: 'Rougher feed sump level transmitter drifted — recalibrated against the pump curve',
  },
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-03-28T09:30',
    end: '2026-03-28T10:30',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'Reagent pump starvation',
    note: 'Reagent pump starved on a blocked suction line — line cleared',
  },
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-04-22T18:00',
    end: '2026-04-22T23:30',
    causeCategory: 'electrical-trips',
    subCause: 'Contactor failure',
    note: 'Rougher blower contactor failed to pick up — contactor replaced',
    workOrderId: 'WO-1190',
  },
  {
    itemId: 'PB-RGH',
    kind: 'unplanned',
    start: '2026-05-01T07:30',
    end: '2026-05-01T09:30',
    causeCategory: 'instrument-failures',
    subCause: 'Flow meter drift',
    note: 'Rougher air flow meter drifted — meter recalibrated',
  },

  // ZN-RGH
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-01-15T12:30',
    end: '2026-01-15T14:00',
    causeCategory: 'instrument-failures',
    subCause: 'pH probe fault',
    note: 'Zn conditioning pH probe drifting — probe replaced and loop recalibrated',
    workOrderId: 'WO-1051',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-01-29T21:00',
    end: '2026-01-30T06:00',
    causeCategory: 'pump-failures',
    subCause: 'Gland seal failure',
    note: 'Zn conditioning pump gland seal failed — seal kit fitted',
    workOrderId: 'WO-1063',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-02-12T10:30',
    end: '2026-02-12T12:00',
    causeCategory: 'instrument-failures',
    subCause: 'Transmitter fault',
    note: 'Zn conditioning pressure transmitter failed — transmitter replaced',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-02-27T19:00',
    end: '2026-02-27T21:00',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'Reagent pump starvation',
    note: 'CuSO4 dosing pump starved on a blocked suction — suction line cleared',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-03-09T13:00',
    end: '2026-03-09T16:00',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'Lime dosing blockage',
    note: 'Lime slaking line blocked at the dosing point — line cleared',
    workOrderId: 'WO-1124',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'planned',
    start: '2026-03-18T06:00',
    end: '2026-03-18T14:00',
    causeCategory: 'reagent-ph-upsets',
    subCause: 'pH probe calibration',
    note: 'Shutdown day — pH probes replaced and control loops recalibrated',
    workOrderId: 'WO-886',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-04-16T22:00',
    end: '2026-04-17T01:00',
    causeCategory: 'instrument-failures',
    subCause: 'pH probe fault',
    note: 'Zn rougher pH probe fouled with scale — probe cleaned',
  },
  {
    itemId: 'ZN-RGH',
    kind: 'unplanned',
    start: '2026-05-05T08:00',
    end: '2026-05-05T11:30',
    causeCategory: 'pump-failures',
    subCause: 'Bearing failure',
    note: 'CuSO4 dosing pump bearing noisy and hot — pump swapped out',
    workOrderId: 'WO-1219',
  },

  // TK03
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-01-07T11:00',
    end: '2026-01-07T12:00',
    causeCategory: 'thickener-paste',
    subCause: 'Flocculant dosing failure',
    note: 'Flocculant dosing pump failed — dosing restored once the pump was replaced',
    workOrderId: 'WO-1043',
  },
  {
    itemId: 'TK03',
    kind: 'planned',
    start: '2026-01-21T06:00',
    end: '2026-01-21T14:00',
    causeCategory: 'thickener-paste',
    subCause: 'Rake drive inspection',
    note: 'Shutdown day — rake drive and lift mechanism inspected',
    workOrderId: 'WO-854',
  },
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-01-27T20:30',
    end: '2026-01-27T23:00',
    causeCategory: 'instrument-failures',
    subCause: 'Transmitter fault',
    note: 'Thickener bed level transmitter drifted — recalibrated against the rake torque',
    workOrderId: 'WO-1060',
  },
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-02-13T09:00',
    end: '2026-02-13T11:00',
    causeCategory: 'thickener-paste',
    subCause: 'Rake drive fault',
    note: 'Rake torque tripped on a solids build-up — rake raised and bed re-established',
    workOrderId: 'WO-1080',
  },
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-03-19T23:00',
    end: '2026-03-20T04:00',
    causeCategory: 'electrical-trips',
    subCause: 'Cable insulation failure',
    note: 'Rake drive earth leakage trip — motor terminal box dried out and re-terminated',
    workOrderId: 'WO-1136',
  },
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-04-02T10:00',
    end: '2026-04-02T11:30',
    causeCategory: 'thickener-paste',
    subCause: 'Paste line blockage',
    note: 'Underflow line blocked at the first bend — line cleared and flushed',
  },
  {
    itemId: 'TK03',
    kind: 'planned',
    start: '2026-04-15T06:00',
    end: '2026-04-15T10:00',
    causeCategory: 'thickener-paste',
    subCause: 'Rake drive inspection',
    note: 'Shutdown day — rake drive gearbox oil changed and drive serviced',
    workOrderId: 'WO-908',
  },
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-04-24T18:30',
    end: '2026-04-24T21:30',
    causeCategory: 'instrument-failures',
    subCause: 'Flow meter drift',
    note: 'Underflow flow meter drifted — meter recalibrated',
  },
  {
    itemId: 'TK03',
    kind: 'unplanned',
    start: '2026-05-08T06:00',
    end: '2026-05-08T09:00',
    causeCategory: 'thickener-paste',
    subCause: 'Underflow line rupture',
    note: 'Underflow line ruptured at a worn section — section replaced',
    workOrderId: 'WO-1225',
  },

  // GP40
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-01-13T13:30',
    end: '2026-01-13T22:30',
    causeCategory: 'pump-failures',
    subCause: 'Gland seal failure',
    note: 'Paste pump seal failed — seal cartridge replaced',
    workOrderId: 'WO-1050',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-01-25T22:00',
    end: '2026-01-25T23:30',
    causeCategory: 'thickener-paste',
    subCause: 'Paste line blockage',
    note: 'Paste line blocked on a coarse batch — line cleared and rheology adjusted',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-02-05T08:30',
    end: '2026-02-05T12:00',
    causeCategory: 'pump-failures',
    subCause: 'Discharge valve leak',
    note: 'Paste pump discharge valve passing — valve replaced',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-02-19T20:00',
    end: '2026-02-19T23:00',
    causeCategory: 'thickener-paste',
    subCause: 'Rake drive fault',
    note: 'Paste plant rake drive faulted on overload — drive reset once the bed was eased',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-03-12T16:30',
    end: '2026-03-13T02:30',
    causeCategory: 'pump-failures',
    subCause: 'Impeller erosion',
    note: 'Paste pump impeller eroded — impeller replaced',
    workOrderId: 'WO-1128',
  },
  {
    itemId: 'GP40',
    kind: 'planned',
    start: '2026-03-18T06:00',
    end: '2026-03-18T12:00',
    causeCategory: 'pump-failures',
    subCause: 'Seal replacement',
    note: 'Shutdown day — paste pump seal kit renewed',
    workOrderId: 'WO-888',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-03-27T01:30',
    end: '2026-03-27T04:30',
    causeCategory: 'thickener-paste',
    subCause: 'Paste line blockage',
    note: 'Paste line blocked after a slow restart — line cleared',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-04-09T12:00',
    end: '2026-04-09T16:00',
    causeCategory: 'pump-failures',
    subCause: 'Suction strainer blockage',
    note: 'Paste pump suction strainer blocked — strainer cleaned',
  },
  {
    itemId: 'GP40',
    kind: 'unplanned',
    start: '2026-05-03T19:30',
    end: '2026-05-04T06:30',
    causeCategory: 'pump-failures',
    subCause: 'Bearing failure',
    note: 'Paste pump bearing failed — pump cartridge replaced',
    workOrderId: 'WO-1216',
  },
];

// ---------------------------------------------------------------------------
// ITEM_BUFFER_HOURS — hours of downstream inventory that absorb a stoppage
// before throughput is lost at the constraint. Durable site configuration.
//
// Five of the eight items copy the register value the item maps to through
// `MaintainableItem.riskId` (strategy.seed.ts); the register itself is owned by
// the risk stream and is deliberately not imported here.
//   AF01 → RK-01 apron feeder drive failure       6 h (fine ore / stockpile)
//   BM01 → RK-02 trunnion bearing failure         0 h (mill is the constraint)
//   GP01 → RK-04 cyclone feed pump failure        0 h (no pump box buffer)
//   ZN-RGH → RK-05 Zn conditioning failure        4 h (conditioning tank)
//   GP40 → RK-09 paste pump failure               1 h (paste plant feed)
// The three items with no register row are this stream's own seed decisions,
// named against the physical inventory that absorbs them.
// ---------------------------------------------------------------------------

export const ITEM_BUFFER_HOURS: Record<DowntimeItemId, number> = {
  CR01: 6, // fine ore bin ahead of the mill
  AF01: 6, // RK-01
  BM01: 0, // RK-02 — grinding is the constraint, nothing absorbs it
  GP01: 0, // RK-04 — no cyclone pump box inventory
  'PB-RGH': 1.5, // Pb rougher feed sump
  'ZN-RGH': 4, // RK-05
  TK03: 4, // thickener inventory
  GP40: 1, // RK-09
};

// ---------------------------------------------------------------------------
// ITEM_SUBPROCESS — which plant-model subprocess each maintainable item sits on
// (`SUBPROCESSES`, bunker-hill-seed.ts). A stoppage on the constraint's own
// subprocess stops throughput for its whole duration; everywhere else an
// intermediate inventory absorbs part of it first (ITEM_BUFFER_HOURS above), so
// this link is what the value-lost read tests before it decides whether to
// subtract a buffer.
//
// `sp-grind` — Primary grinding (BM01 + CY01) — is the plant's single
// bottleneck: the only subprocess seeded `isBottleneck`, with BM01 and GP01 the
// two items on it. The cost stream's budget-impact rule reads the same flag
// through the risk register; this file types the link, it does not re-derive
// the flag.
//
// Durable: the equipment-to-flowsheet mapping is entered once. No derivation
// here — `itemAtConstraint` reads it (ValueLost.transform.ts).
// ---------------------------------------------------------------------------

export const ITEM_SUBPROCESS: Record<DowntimeItemId, string> = {
  CR01: 'sp-crush', // primary crushing
  AF01: 'sp-crush', // apron feeder, ore receiving
  BM01: 'sp-grind', // the constraint subprocess
  GP01: 'sp-grind', // cyclone feed, the constraint subprocess
  'PB-RGH': 'sp-pb', // Pb/Ag rougher flotation
  'ZN-RGH': 'sp-zn', // Zn rougher flotation
  TK03: 'sp-paste', // tails thickening
  GP40: 'sp-paste', // paste backfill pumps
};

// ---------------------------------------------------------------------------
// EQUIPMENT_CLASSES — the reliability read's grouping. The four targets are the
// only typed numbers that read shows; actual MTBF, actual MTTR, the gap and the
// trend are all computed. Every DOWNTIME_ITEMS id belongs to exactly one class.
// ---------------------------------------------------------------------------

export type EquipmentClass = {
  id: string;
  label: string;
  itemIds: readonly DowntimeItemId[];
  targetMtbfHours: number;
  targetMttrHours: number;
};

export const EQUIPMENT_CLASSES: readonly EquipmentClass[] = [
  { id: 'crushing-feed', label: 'Crushing & feed', itemIds: ['CR01', 'AF01'], targetMtbfHours: 400, targetMttrHours: 4 },
  { id: 'grinding', label: 'Grinding', itemIds: ['BM01'], targetMtbfHours: 600, targetMttrHours: 6 },
  { id: 'pumps', label: 'Pumps', itemIds: ['GP01', 'GP40'], targetMtbfHours: 300, targetMttrHours: 3 },
  { id: 'flotation', label: 'Flotation', itemIds: ['PB-RGH', 'ZN-RGH'], targetMtbfHours: 500, targetMttrHours: 4 },
  { id: 'thickening', label: 'Thickening', itemIds: ['TK03'], targetMtbfHours: 700, targetMttrHours: 5 },
];

/** Durable: the plant's planned-maintenance benchmark, and the cumulative share
 *  the cause Pareto cuts at. Targets, not results — the ratios are computed. */
export const PLANNED_RATIO_TARGET_PCT = 80;
export const PARETO_CUT_PCT = 80;

/** Durable: a class whose actual MTBF is below this share of its target reads
 *  `bad`; at or above it but under target reads `warn`; at target `ok`. */
export const MTBF_WARN_PCT = 75;
/** Durable: a trailing-30-day MTBF within this share of the preceding 30 days'
 *  reads `stable`; outside it, the sign of the difference decides. */
export const TREND_STABLE_BAND_PCT = 10;
